import { useState, useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import type { EngineError, EngineErrorCode, SyncLogEntry, SyncProgress, WorkspaceSyncPolicy } from '../engine/types'
import { timeAgo } from '../utils/writerUtils'
import './SyncDetailPopover.css'

const SQLITE_EXTENSIONS = ['.db', '.sqlite', '.sqlite3', '.s3db', '.sdb'] as const

const ERROR_HINTS: Record<EngineErrorCode, string> = {
    NETWORK_ERROR: 'Check your internet connection.',
    CAS_MISMATCH: 'Another device updated. Merge will be attempted.',
    FORBIDDEN: 'No write access. Ask owner for read-write link.',
    CORRUPT_DATA: 'Data may be corrupted. Try clearing cache.',
    NOT_FOUND: 'Workspace not found. May have been deleted.',
    ABORTED: 'Sync was cancelled. Try again.',
    COMMIT_INVALID: 'Commit was rejected by server. Check debug info.',
    BLOB_SOFT_DELETED: 'This workspace has been permanently deleted.',
    OBJECT_MISSING: 'Server lost objects. Recovery sync in progress.',
    UNSUPPORTED_ALGORITHM: 'Upgrade the app or reopen the workspace with a newer client.',
    UNSUPPORTED_STATE_FORMAT: 'This workspace uses a newer state format than this client understands.',
    CORRUPTED_MANIFEST: 'A required content manifest is corrupted. Retry sync or recover from another device.',
}

const LOG_ICONS: Record<SyncLogEntry['type'], [string, string]> = {
    'sync-start': ['↑', 'log-start'],
    'sync-ok': ['✓', 'log-ok'],
    'sync-error': ['✗', 'log-error'],
    'pull-ok': ['↓', 'log-pull'],
    'pull-skip': ['·', 'log-skip'],
}

function formatTime(epoch: number | undefined): string {
    if (!epoch) return '—'
    const d = new Date(epoch)
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

function isSqliteConflictPath(path: string): boolean {
    const lower = path.toLowerCase()
    return SQLITE_EXTENSIONS.some(ext => lower.endsWith(ext))
}

interface SyncDetailPopoverProps {
    syncing: boolean
    connectivity: 'connected' | 'offline' | 'unreachable'
    synced: boolean
    lastSyncAt: number
    pendingChanges: number
    lastSyncError?: EngineError
    syncLog?: readonly SyncLogEntry[]
    conflictPaths?: string[]
    syncProgress?: SyncProgress
    syncPaused?: boolean
    syncPolicy?: WorkspaceSyncPolicy
    syncNow: () => Promise<void>
    pauseSync: () => void
    resumeSync: () => void
    onOpenPath?: (path: string) => void
    onClose: () => void
}

export default function SyncDetailPopover({
    syncing,
    connectivity,
    synced,
    lastSyncAt,
    pendingChanges,
    lastSyncError,
    syncLog = [],
    conflictPaths = [],
    syncProgress,
    syncPaused,
    syncPolicy = 'on-demand',
    syncNow,
    pauseSync,
    resumeSync,
    onClose,
    onOpenPath,
}: SyncDetailPopoverProps) {
    const [logOpen, setLogOpen] = useState(false)
    const [conflictsOpen, setConflictsOpen] = useState(true)
    const [expandedEntries, setExpandedEntries] = useState<Set<number>>(new Set())
    const popoverRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        function handleClick(e: MouseEvent) {
            if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
                onClose()
            }
        }
        function handleKey(e: KeyboardEvent) {
            if (e.key === 'Escape') onClose()
        }
        document.addEventListener('mousedown', handleClick)
        document.addEventListener('keydown', handleKey)
        return () => {
            document.removeEventListener('mousedown', handleClick)
            document.removeEventListener('keydown', handleKey)
        }
    }, [onClose])

    const handleSync = useCallback(() => {
        syncNow().catch(() => { })
    }, [syncNow])

    const toggleEntry = useCallback((id: number) => {
        setExpandedEntries(prev => {
            const next = new Set(prev)
            if (next.has(id)) {
                next.delete(id)
            } else {
                next.add(id)
            }
            return next
        })
    }, [])

    const handleOpenPath = useCallback((path: string) => {
        onOpenPath?.(path)
        onClose()
    }, [onClose, onOpenPath])

    const statusDotClass = connectivity === 'unreachable' ? 'unreachable' : lastSyncError ? 'error' : connectivity === 'connected' ? 'online' : 'offline'
    const statusText = connectivity === 'unreachable'
        ? 'Server unreachable'
        : lastSyncError
            ? 'Sync error'
            : syncPaused
                ? 'Sync paused'
                : syncing && syncProgress
                    ? `Uploading · ${syncProgress.objectsUploaded}/${syncProgress.objectsTotal}`
                    : syncing
                        ? 'Syncing…'
                        : synced
                            ? syncPolicy === 'auto' ? 'Auto-sync' : 'On-demand sync'
                            : pendingChanges > 0
                                ? `${pendingChanges} pending`
                                : 'Idle'

    const progressPct = syncProgress && syncProgress.objectsTotal > 0
        ? Math.round((syncProgress.objectsUploaded / syncProgress.objectsTotal) * 100)
        : 0

    const formatBytes = (bytes: number): string => {
        if (bytes < 1024) return `${bytes} B`
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    }

    const uniqueConflictPaths = Array.from(new Set(conflictPaths))
    const conflictCount = uniqueConflictPaths.length

    return createPortal(
        <>
            <div className="sync-popover-backdrop" onClick={onClose} />
            <div className="sync-popover" ref={popoverRef} onClick={e => e.stopPropagation()}>
                <div className="sync-popover-header">
                    <span className={`sync-popover-dot ${statusDotClass}`} />
                    <span className="sync-popover-status">{statusText}</span>
                    {lastSyncAt > 0 && <span className="sync-popover-ago">· {timeAgo(lastSyncAt, { fallback: 'never', maxDepth: 'seconds', suffix: ' ago' })}</span>}
                </div>

                <div className="sync-popover-details">
                    <div className="sync-popover-row">
                        <span className="sync-popover-label">Connectivity</span>
                        <span className={`sync-popover-value ${connectivity === 'connected' ? 'val-ok' : 'val-warn'}`}>
                            {connectivity === 'connected' ? 'Connected' : connectivity === 'offline' ? 'Offline' : 'Unreachable'}
                        </span>
                    </div>
                    <div className="sync-popover-row">
                        <span className="sync-popover-label">Pending changes</span>
                        <span className="sync-popover-value">{pendingChanges}</span>
                    </div>
                    <div className="sync-popover-row">
                        <span className="sync-popover-label">Conflicts</span>
                        <span className={`sync-popover-value ${conflictCount > 0 ? 'val-warn' : ''}`}>
                            {conflictCount > 0 ? conflictCount : 'none'}
                        </span>
                    </div>
                </div>

                {conflictCount > 0 && (
                    <div className="sync-popover-conflicts">
                        <button
                            className="sync-popover-conflicts-toggle"
                            onClick={() => setConflictsOpen(open => !open)}
                        >
                            <span className="sync-popover-log-chevron">{conflictsOpen ? '▾' : '▸'}</span>
                            Conflicted paths ({conflictCount})
                        </button>
                        {conflictsOpen && (
                            <div className="sync-popover-conflict-list">
                                {uniqueConflictPaths.map(path => {
                                    const sqlitePath = isSqliteConflictPath(path)
                                    return (
                                        <div key={path} className="sync-popover-conflict-item">
                                            <div className="sync-popover-conflict-main">
                                                <div className="sync-popover-conflict-path">{path}</div>
                                                <div className="sync-popover-conflict-meta">
                                                    {sqlitePath && (
                                                        <span className="sync-popover-conflict-pill">SQLite</span>
                                                    )}
                                                </div>
                                            </div>
                                            {onOpenPath && (
                                                <button
                                                    className="sync-popover-conflict-open"
                                                    onClick={() => handleOpenPath(path)}
                                                    aria-label={`Open ${path}`}
                                                >
                                                    Open
                                                </button>
                                            )}
                                        </div>
                                    )
                                })}
                            </div>
                        )}
                    </div>
                )}

                {lastSyncError && (
                    <div className="sync-popover-error">
                        <div className="sync-popover-error-header">
                            <span className="sync-popover-error-icon">⚠</span>
                            <span className="sync-popover-error-badge">{lastSyncError.code}</span>
                        </div>
                        <p className="sync-popover-error-msg">{lastSyncError.message}</p>
                        <p className="sync-popover-error-hint">
                            💡 {ERROR_HINTS[lastSyncError.code] || 'An unexpected error occurred.'}
                        </p>
                        {lastSyncError.details && (
                            <div className="sync-popover-debug">
                                <details>
                                    <summary>Debug Info</summary>
                                    <pre>{JSON.stringify(lastSyncError.details, null, 2)}</pre>
                                    <button
                                        className="sync-popover-copy-btn"
                                        onClick={(e) => {
                                            e.preventDefault()
                                            navigator.clipboard.writeText(JSON.stringify(lastSyncError, null, 2))
                                            const btn = e.target as HTMLButtonElement
                                            const original = btn.innerText
                                            btn.innerText = 'Copied!'
                                            setTimeout(() => btn.innerText = original, 2000)
                                        }}
                                    >
                                        Copy Error Details
                                    </button>
                                </details>
                            </div>
                        )}
                    </div>
                )}

                {(syncProgress || (syncPaused && pendingChanges > 0)) && (
                    <div className="sync-popover-progress">
                        <div className="sync-progress-track">
                            <div
                                className={`sync-progress-bar ${syncPaused ? 'paused' : ''}`}
                                style={{ width: `${progressPct}%` }}
                            />
                        </div>
                        <div className="sync-progress-info">
                            <span>{progressPct}%</span>
                            {syncProgress && (
                                <span className="sync-progress-bytes">
                                    {formatBytes(syncProgress.bytesUploaded)} / {formatBytes(syncProgress.bytesTotal)}
                                </span>
                            )}
                        </div>
                    </div>
                )}

                {(syncing || syncPaused) && (
                    <div className="sync-popover-controls">
                        {syncing && !syncPaused ? (
                            <button className="sync-popover-ctrl-btn" onClick={pauseSync}>
                                ⏸ Pause
                            </button>
                        ) : syncPaused ? (
                            <button className="sync-popover-ctrl-btn sync-ctrl-resume" onClick={resumeSync}>
                                ▶ Resume
                            </button>
                        ) : null}
                    </div>
                )}

                {!syncing && !syncPaused && (
                    <button
                        className="sync-popover-btn"
                        onClick={handleSync}
                    >
                        {lastSyncError ? (
                            <>↻ Retry sync</>
                        ) : (
                            <>↻ Sync now</>
                        )}
                    </button>
                )}

                {syncLog.length > 0 && (
                    <div className="sync-popover-log-section">
                        <button
                            className="sync-popover-log-toggle"
                            onClick={() => setLogOpen(v => !v)}
                        >
                            <span className="sync-popover-log-chevron">{logOpen ? '▾' : '▸'}</span>
                            Activity log ({syncLog.length})
                        </button>

                        {logOpen && (
                            <div className="sync-popover-log-list">
                                {[...syncLog].reverse().map(entry => {
                                    const [icon, cls] = LOG_ICONS[entry.type] || ['·', 'log-skip']
                                    const hasFiles = entry.affectedPaths && entry.affectedPaths.length > 0
                                    const isExpanded = expandedEntries.has(entry.id)
                                    return (
                                        <div key={entry.id} className={`sync-popover-log-entry ${cls}`}>
                                            <div
                                                className={`sync-log-row ${hasFiles ? 'expandable' : ''}`}
                                                onClick={hasFiles ? () => toggleEntry(entry.id) : undefined}
                                            >
                                                <span className="sync-log-time">{formatTime(entry.timestamp)}</span>
                                                <span className="sync-log-icon">{icon}</span>
                                                <span className="sync-log-text">
                                                    {entry.detail || entry.type}
                                                    {entry.durationMs != null && (
                                                        <span className="sync-log-duration"> ({entry.durationMs}ms)</span>
                                                    )}
                                                </span>
                                                {hasFiles && <span className="sync-log-expand-hint">{isExpanded ? '▾' : '▸'}</span>}
                                            </div>
                                            {hasFiles && isExpanded && (
                                                <div className="sync-log-files">
                                                    {entry.affectedPaths!.map((p, i) => (
                                                        <div key={i} className="sync-log-file">{p.split('/').pop() || p}</div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )
                                })}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </>,
        document.body
    )
}
